import React, { useState, useCallback, useMemo } from 'react'
import {
  X,
  ScanBarcode,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Book,
  User,
  Calendar,
  Hash,
  DollarSign,
  Package,
  Tag,
  Printer,
  ImagePlus,
  Link as LinkIcon,
  Upload,
  Plus,
  Percent,
  Layers
} from 'lucide-react'
import { BarcodeScanner } from './BarcodeScanner'
import { BarcodeGenerator } from './BarcodeGenerator'
import { CategoryTreeDropdown } from './CategoryTreeDropdown'
import { lookupBookByISBN, generateBookSKU, isValidISBN, BookDetails } from '@/services/bookLookupService'
import { Product, ProductCategory, SalesUnit } from '@/types/pos'
import { usePOSStore } from '@/store/posStore'
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner'

interface AddProductModalProps {
  categories: ProductCategory[]
  existingSKUs: string[]
  onSave: (product: Partial<Product>) => Promise<void>
  onClose: () => void
}

type Step = 'scan' | 'details' | 'pricing'

export const AddProductModal: React.FC<AddProductModalProps> = ({
  categories,
  existingSKUs,
  onSave,
  onClose,
}) => {
  const [step, setStep] = useState<Step>('scan')
  const [showCameraScanner, setShowCameraScanner] = useState(false)
  const [showBarcodeGenerator, setShowBarcodeGenerator] = useState(false)
  const [manualISBN, setManualISBN] = useState('')
  const [isLookingUp, setIsLookingUp] = useState(false)
  const [lookupError, setLookupError] = useState<string | null>(null)
  const [bookDetails, setBookDetails] = useState<BookDetails | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Variant groups from store
  const storeProducts = usePOSStore(s => s.products)
  const variantGroups = useMemo(() => {
    const groups = new Map<string, string>()
    for (const p of storeProducts) {
      if (p.variantGroupId && p.variantGroupName) {
        groups.set(p.variantGroupId, p.variantGroupName)
      }
    }
    return Array.from(groups.entries()).map(([id, name]) => ({ id, name }))
  }, [storeProducts])

  // UoM state
  const [baseUnit, setBaseUnit] = useState('')
  const [salesUnits, setSalesUnits] = useState<SalesUnit[]>([])
  const [showUoM, setShowUoM] = useState(false)

  // Product form state
  const [productData, setProductData] = useState<Partial<Product>>({
    name: '',
    description: '',
    sku: '',
    barcode: '',
    isbn: '',
    // Allow creating an item without an initial price; it can be set later
    price: undefined,
    cost: 0,
    stock: 1,
    minStock: 5,
    isActive: true,
    category: categories[0] || undefined,
    tags: [],
    image: '', // Initialize image field
  })

  // Handle barcode scan
  const handleBarcodeScan = useCallback(async (barcode: string) => {
    if (isLookingUp) return

    setLookupError(null)
    setIsLookingUp(true)
    setManualISBN(barcode)

    try {
      // Validate ISBN format
      if (!isValidISBN(barcode)) {
        // If not a valid ISBN, just use it as barcode without lookup
        setProductData(prev => ({
          ...prev,
          barcode,
          sku: generateBookSKU('PRODUCT', existingSKUs),
        }))
        setStep('details')
        setIsLookingUp(false)
        return
      }

      // Lookup book details
      const details = await lookupBookByISBN(barcode)

      if (details) {
        setBookDetails(details)

        // Auto-fill product data
        const generatedSKU = generateBookSKU(details.title, existingSKUs)
        setProductData(prev => ({
          ...prev,
          name: details.title,
          description: details.author ? `By ${details.author}${details.publisher ? ` • Published by ${details.publisher}` : ''}${details.publishDate ? ` (${details.publishDate})` : ''}` : '',
          sku: generatedSKU,
          barcode: details.isbn,
          isbn: details.isbn,
          image: details.coverImage || '📖',
          tags: details.subjects || [],
        }))

        setStep('pricing')
      } else {
        // Book not found in database, allow manual entry
        setLookupError('Book not found in database. Please enter details manually.')
        setProductData(prev => ({
          ...prev,
          barcode,
          isbn: barcode,
          sku: generateBookSKU('BOOK', existingSKUs),
        }))
        setStep('details')
      }
    } catch (error) {
      console.error('ISBN lookup error:', error)
      setLookupError('Failed to lookup book. Please enter details manually.')
      setProductData(prev => ({
        ...prev,
        barcode,
        isbn: barcode,
        sku: generateBookSKU('BOOK', existingSKUs),
      }))
      setStep('details')
    } finally {
      setIsLookingUp(false)
    }
  }, [isLookingUp, existingSKUs])

  // USB Scanner support
  useBarcodeScanner({
    onScan: handleBarcodeScan,
    enabled: step === 'scan' && !showCameraScanner && !isLookingUp,
  })

  const handleManualISBNSubmit = () => {
    if (manualISBN.trim()) {
      handleBarcodeScan(manualISBN.trim())
    }
  }

  const handleSave = async () => {
    // Only product name is mandatory; price can be left empty and set later
    if (!productData.name) {
      return
    }

    const payload: Partial<Product> = { ...productData }
    if (salesUnits.length > 0 && baseUnit) {
      payload.baseUnit = baseUnit
      payload.salesUnits = salesUnits
    }
    if (payload.volumePricing?.rules?.length) {
      const validRules = payload.volumePricing.rules.filter(
        r => typeof r.blockSize === 'number' && r.blockSize >= 1 && typeof r.pricePerUnit === 'number' && r.pricePerUnit >= 0
      )
      payload.volumePricing = validRules.length ? { rules: validRules } : undefined
    }

    setIsSubmitting(true)
    try {
      await onSave(payload)
      onClose()
    } catch (error) {
      console.error('Save failed:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderScanStep = () => (
    <div className="space-y-6">
      {/* Scan Instructions */}
      <div className="text-center py-8">
        <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <ScanBarcode className="w-10 h-10 text-primary" />
        </div>
        <h3 className="text-xl font-bold mb-2">Scan Product Barcode</h3>
        <p className="text-muted-foreground text-sm max-w-sm mx-auto">
          Scan the ISBN barcode on the book to automatically fetch its details, or enter it manually below.
        </p>
      </div>

      {/* USB Scanner Status */}
      <div className="p-3 bg-primary/5 border border-primary/20 rounded-xl text-center">
        <p className="text-sm text-primary font-medium">
          📡 USB Scanner Ready - Just scan the barcode!
        </p>
      </div>

      {/* Manual Entry */}
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Or enter ISBN manually</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={manualISBN}
            onChange={(e) => setManualISBN(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleManualISBNSubmit()}
            placeholder="Enter ISBN (e.g., 9780141036144)"
            className="flex-1 px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none font-mono"
            disabled={isLookingUp}
          />
          <button
            onClick={handleManualISBNSubmit}
            disabled={!manualISBN.trim() || isLookingUp}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold disabled:opacity-50 transition-colors"
          >
            {isLookingUp ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Lookup'}
          </button>
        </div>
      </div>

      {/* Camera Scanner Button */}
      <button
        onClick={() => setShowCameraScanner(true)}
        disabled={isLookingUp}
        className="w-full py-4 bg-muted hover:bg-muted/80 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
      >
        <ScanBarcode className="w-5 h-5" />
        Use Camera Scanner
      </button>

      {/* Skip to Manual Entry */}
      <button
        onClick={() => {
          setProductData(prev => ({
            ...prev,
            sku: generateBookSKU('BOOK', existingSKUs),
          }))
          setStep('details')
        }}
        className="w-full py-3 text-muted-foreground hover:text-foreground transition-colors text-sm"
      >
        Skip scanning - Enter details manually
      </button>

      {/* Loading State */}
      {isLookingUp && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <p className="font-semibold">Looking up product details...</p>
            <p className="text-sm text-muted-foreground">Searching Open Library database</p>
          </div>
        </div>
      )}
    </div>
  )

  const renderDetailsStep = () => (
    <div className="space-y-4">
      {lookupError && (
        <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-yellow-600 dark:text-yellow-400">{lookupError}</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        {/* Book Title */}
        <div className="col-span-2">
          <label className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase mb-1">
            <Book className="w-3 h-3" /> Book Title *
          </label>
          <input
            required
            value={productData.name}
            onChange={(e) => setProductData({ ...productData, name: e.target.value })}
            placeholder="Enter book title"
            className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
          />
        </div>

        {/* Author / Description */}
        <div className="col-span-2">
          <label className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase mb-1">
            <User className="w-3 h-3" /> Author / Description
          </label>
          <input
            value={productData.description}
            onChange={(e) => setProductData({ ...productData, description: e.target.value })}
            placeholder="e.g., By Author Name • Publisher"
            className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
          />
        </div>

        {/* SKU (Auto-generated) */}
        <div>
          <label className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase mb-1">
            <Hash className="w-3 h-3" /> SKU (Auto)
          </label>
          <input
            value={productData.sku}
            onChange={(e) => setProductData({ ...productData, sku: e.target.value })}
            className="w-full px-4 py-2 bg-background border border-border rounded-lg font-mono text-sm"
            readOnly
          />
        </div>

        {/* Barcode/ISBN */}
        <div>
          <label className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase mb-1">
            <ScanBarcode className="w-3 h-3" /> Barcode/ISBN
          </label>
          <div className="flex gap-2">
            <input
              value={productData.barcode || productData.isbn || ''}
              onChange={(e) => setProductData({ ...productData, barcode: e.target.value, isbn: e.target.value })}
              placeholder="Enter barcode or ISBN"
              className="flex-1 px-4 py-2 bg-background border border-border rounded-lg font-mono text-sm"
            />
            {!productData.barcode && !productData.isbn && productData.sku && (
              <button
                onClick={() => {
                  // Generate a numeric barcode from SKU (max 8 digits)
                  const skuNumeric = (productData.sku || '').replace(/\D/g, '') || Date.now().toString().slice(-8)
                  const paddedBarcode = skuNumeric.padStart(8, '0').slice(0, 8)
                  setProductData({ ...productData, barcode: paddedBarcode, isbn: paddedBarcode })
                }}
                className="px-3 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors text-xs font-semibold whitespace-nowrap"
                title="Generate barcode from SKU"
              >
                Auto
              </button>
            )}
          </div>
        </div>

        {/* Category */}
        <div className="col-span-2">
          <label className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase mb-1">
            <Tag className="w-3 h-3" /> Category / Genre
          </label>
          <CategoryTreeDropdown
            categories={categories}
            value={
              productData.category == null
                ? 'all'
                : typeof productData.category === 'string'
                  ? productData.category
                  : (productData.category?.id || (productData.category as any)?._id || 'all')
            }
            onSelect={(id) => {
              const cat = id === 'all' || !id ? undefined : categories.find(c => (c.id || (c as any)._id) === id)
              setProductData({ ...productData, category: cat })
            }}
            allLabel="No category"
            placeholder="Genre"
          />
        </div>

        {/* Variant Group */}
        <div className="col-span-2">
          <label className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase mb-1">
            <Layers className="w-3 h-3 text-violet-400" /> Variant Group
          </label>
          <p className="text-[9px] text-muted-foreground mb-1.5">Link products that are the same item in different colors/sizes</p>
          <select
            value={productData.variantGroupId || '_none'}
            onChange={e => {
              const val = e.target.value
              if (val === '_none') {
                setProductData({ ...productData, variantGroupId: undefined, variantGroupName: undefined })
              } else if (val === '_new') {
                const name = prompt('Enter variant group name (e.g. "A4 Paper"):')
                if (name && name.trim()) {
                  setProductData({
                    ...productData,
                    variantGroupId: `vg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                    variantGroupName: name.trim(),
                  })
                }
              } else {
                const group = variantGroups.find(g => g.id === val)
                if (group) {
                  setProductData({ ...productData, variantGroupId: group.id, variantGroupName: group.name })
                }
              }
            }}
            className="w-full px-4 py-2 bg-background border border-border rounded-lg"
          >
            <option value="_none">None (standalone product)</option>
            <option value="_new">+ Create New Group...</option>
            {variantGroups.map(g => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </div>

        {/* Book Cover Image */}
        <div className="col-span-2">
          <label className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase mb-1">
            <ImagePlus className="w-3 h-3" /> Book Cover Image
          </label>
          <div className="flex gap-3">
            {/* Image Preview */}
            <div className="w-20 h-28 bg-muted/20 border border-border rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
              {productData.image?.startsWith('http') || productData.image?.startsWith('data:') ? (
                <img
                  src={productData.image}
                  alt="Book cover"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none'
                  }}
                />
              ) : (
                <span className="text-3xl text-muted-foreground">{productData.image || '📖'}</span>
              )}
            </div>

            {/* Upload Options */}
            <div className="flex-1 space-y-2">
              {/* URL Input */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="url"
                    value={productData.image?.startsWith('http') ? productData.image : ''}
                    onChange={(e) => setProductData({ ...productData, image: e.target.value })}
                    placeholder="Paste image URL..."
                    className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                  />
                </div>
              </div>

              {/* File Upload */}
              <div className="flex gap-2">
                <label className="flex-1 px-4 py-2 bg-muted/50 hover:bg-muted border border-border rounded-lg cursor-pointer flex items-center justify-center gap-2 transition-colors">
                  <Upload className="w-4 h-4" />
                  <span className="text-sm font-medium">Upload Image</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) {
                        // Check file size (max 2MB for base64)
                        if (file.size > 2 * 1024 * 1024) {
                          alert('Image size must be less than 2MB')
                          return
                        }
                        const reader = new FileReader()
                        reader.onloadend = () => {
                          setProductData({ ...productData, image: reader.result as string })
                        }
                        reader.readAsDataURL(file)
                      }
                    }}
                  />
                </label>
                {productData.image && (
                  <button
                    type="button"
                    onClick={() => setProductData({ ...productData, image: '' })}
                    className="px-3 py-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors text-sm"
                  >
                    Clear
                  </button>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground">
                Supports URL or upload (max 2MB). Images auto-fetch from ISBN lookup.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <button
          onClick={() => setStep('scan')}
          className="flex-1 py-3 bg-muted hover:bg-muted/80 rounded-xl font-semibold"
        >
          Back
        </button>
        <button
          onClick={() => setStep('pricing')}
          disabled={!productData.name}
          className="flex-1 py-3 bg-primary text-primary-foreground rounded-xl font-semibold disabled:opacity-50"
        >
          Continue to Pricing
        </button>
      </div>
    </div>
  )

  const renderPricingStep = () => (
    <div className="space-y-4">
      {/* Book Preview with Image Upload */}
      <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
        <div className="flex gap-4">
          {/* Editable Image */}
          <div className="relative group">
            <div className="w-20 h-28 bg-muted/30 rounded-lg flex items-center justify-center overflow-hidden border-2 border-dashed border-border group-hover:border-primary transition-colors">
              {(productData.image?.startsWith('http') || productData.image?.startsWith('data:')) ? (
                <img
                  src={productData.image}
                  alt={productData.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none'
                  }}
                />
              ) : (
                <span className="text-3xl">{productData.image || '📖'}</span>
              )}
            </div>
            {/* Upload Overlay */}
            <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer rounded-lg">
              <div className="text-white text-center">
                <Upload className="w-5 h-5 mx-auto mb-1" />
                <span className="text-[10px]">Change</span>
              </div>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    if (file.size > 2 * 1024 * 1024) {
                      alert('Image size must be less than 2MB')
                      return
                    }
                    const reader = new FileReader()
                    reader.onloadend = () => {
                      setProductData({ ...productData, image: reader.result as string })
                    }
                    reader.readAsDataURL(file)
                  }
                }}
              />
            </label>
          </div>

          {/* Book Details */}
          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-sm truncate">{productData.name}</h4>
            <p className="text-xs text-muted-foreground truncate">{productData.description}</p>
            <p className="text-xs text-primary font-mono mt-2">{productData.sku}</p>
            {productData.barcode && (
              <p className="text-xs text-muted-foreground font-mono">ISBN: {productData.barcode}</p>
            )}
            {/* Image URL input */}
            <div className="mt-2">
              <input
                type="url"
                value={(productData.image?.startsWith('http')) ? productData.image : ''}
                onChange={(e) => setProductData({ ...productData, image: e.target.value })}
                placeholder="Or paste image URL..."
                className="w-full px-2 py-1 bg-background/50 border border-border rounded text-[10px] focus:ring-1 focus:ring-primary focus:outline-none"
              />
            </div>
          </div>
          <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
        </div>
      </div>

      {/* Pricing Fields */}
      <div className="space-y-3">
        <p className="text-xs font-black uppercase text-muted-foreground tracking-widest">Pricing</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase mb-1">
              <DollarSign className="w-3 h-3" /> Retail Price (LKR)
            </label>
            <p className="text-[9px] text-muted-foreground -mt-0.5 mb-1 pl-0.5">Leave empty to set price later via GRN</p>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">Rs.</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={productData.price ?? ''}
                onChange={(e) => {
                  const value = e.target.value
                  const num = parseFloat(value)
                  setProductData({
                    ...productData,
                    price: value === '' ? undefined : (!isNaN(num) && num >= 0 ? num : 0),
                  })
                }}
                placeholder="0.00"
                className="w-full pl-10 pr-4 py-3 bg-background border border-border rounded-lg text-lg font-bold focus:ring-2 focus:ring-primary focus:outline-none"
                autoFocus
              />
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase mb-1">
              <DollarSign className="w-3 h-3" /> Cost Price (LKR)
            </label>
            <p className="text-[9px] text-muted-foreground -mt-0.5 mb-1 pl-0.5">Purchase price from supplier</p>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">Rs.</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={productData.cost || ''}
                onChange={(e) => setProductData({ ...productData, cost: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
                className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase mb-1">
              Wholesale Price (LKR)
            </label>
            <p className="text-[9px] text-muted-foreground -mt-0.5 mb-1 pl-0.5">Price for bulk selling</p>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">Rs.</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={productData.wholesalePrice || ''}
                onChange={(e) => setProductData({ ...productData, wholesalePrice: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
                className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase mb-1">
              Discount Price (LKR)
            </label>
            <p className="text-[9px] text-muted-foreground -mt-0.5 mb-1 pl-0.5">Active promotion price (auto-applies at POS)</p>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">Rs.</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={productData.discountPrice || ''}
                onChange={(e) => setProductData({ ...productData, discountPrice: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
                className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
              />
            </div>
          </div>

          <div className="col-span-2">
            <label className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase mb-1">
              Marked Price (LKR)
            </label>
            <p className="text-[9px] text-muted-foreground -mt-0.5 mb-1 pl-0.5">Manufacturer's issued price (display only)</p>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">Rs.</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={productData.markedPrice || ''}
                onChange={(e) => setProductData({ ...productData, markedPrice: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
                className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
              />
            </div>
          </div>

          {/* Volume pricing (customer) */}
          <div className="col-span-2">
            <div className="flex items-center gap-2 mb-1">
              <Percent className="w-3 h-3 text-primary" />
              <label className="text-xs font-bold text-muted-foreground uppercase">Volume Pricing (Customer)</label>
            </div>
            <p className="text-[9px] text-muted-foreground mb-2 pl-0.5">e.g. Every 500 units @ Rs.2.50 each; remainder at retail.</p>
            <div className="space-y-2 p-3 bg-muted/20 border border-border/50 rounded-lg">
              {(productData.volumePricing?.rules ?? []).map((rule, idx) => (
                <div key={idx} className="flex gap-2 items-end flex-wrap">
                  <div className="flex-1 min-w-[90px]">
                    <span className="text-[10px] text-muted-foreground">Every (qty)</span>
                    <input
                      type="number"
                      min={1}
                      value={rule.blockSize || ''}
                      onChange={(e) => {
                        const rules = [...(productData.volumePricing?.rules ?? [])]
                        const val = parseInt(e.target.value, 10)
                        if (rules[idx]) rules[idx] = { ...rules[idx], blockSize: isNaN(val) || val < 1 ? 1 : val }
                        else rules[idx] = { blockSize: isNaN(val) || val < 1 ? 1 : val, pricePerUnit: rule.pricePerUnit ?? 0 }
                        setProductData({ ...productData, volumePricing: { rules } })
                      }}
                      placeholder="500"
                      className="w-full mt-0.5 px-2 py-1.5 bg-background border border-border rounded text-sm"
                    />
                  </div>
                  <div className="flex-1 min-w-[90px]">
                    <span className="text-[10px] text-muted-foreground">Rs./unit</span>
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      value={rule.pricePerUnit ?? ''}
                      onChange={(e) => {
                        const rules = [...(productData.volumePricing?.rules ?? [])]
                        const val = parseFloat(e.target.value)
                        if (rules[idx]) rules[idx] = { ...rules[idx], pricePerUnit: isNaN(val) || val < 0 ? 0 : val }
                        else rules[idx] = { blockSize: rule.blockSize || 1, pricePerUnit: isNaN(val) || val < 0 ? 0 : val }
                        setProductData({ ...productData, volumePricing: { rules } })
                      }}
                      placeholder="2.50"
                      className="w-full mt-0.5 px-2 py-1.5 bg-background border border-border rounded text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const rules = (productData.volumePricing?.rules ?? []).filter((_, i) => i !== idx)
                      setProductData({ ...productData, volumePricing: rules.length ? { rules } : undefined })
                    }}
                    className="p-1.5 text-red-500 hover:bg-red-500/10 rounded transition-colors"
                    title="Remove rule"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  const rules = [...(productData.volumePricing?.rules ?? []), { blockSize: 1, pricePerUnit: 0 }]
                  setProductData({ ...productData, volumePricing: { rules } })
                }}
                className="text-xs font-semibold text-primary hover:underline flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Add volume rule
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Unit of Measure */}
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => setShowUoM(v => !v)}
          className="flex items-center gap-2 text-xs font-black uppercase text-muted-foreground tracking-widest hover:text-foreground transition-colors"
        >
          <Package className="w-3 h-3 text-primary" />
          Unit of Measure (UoM)
          <span className="ml-1 text-primary">{showUoM ? '▲' : '▼'}</span>
          {salesUnits.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-primary/10 text-primary rounded text-[10px]">{salesUnits.length} unit{salesUnits.length > 1 ? 's' : ''}</span>
          )}
        </button>
        <p className="text-[9px] text-muted-foreground pl-0.5">
          For products sold in multiple forms (e.g. A4 Bundle + Loose + Printing) that share the same stock.
        </p>
        {showUoM && (
          <div className="space-y-3 p-3 bg-muted/20 border border-border/50 rounded-lg">
            <div>
              <label className="text-[10px] text-muted-foreground font-semibold block mb-1">Base Unit (smallest trackable unit)</label>
              <input
                type="text"
                value={baseUnit}
                onChange={(e) => setBaseUnit(e.target.value)}
                placeholder="e.g. sheet, mug, piece, ml"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:outline-none"
              />
            </div>
            {salesUnits.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] text-muted-foreground uppercase">
                      <th className="text-left pb-1 pr-2">Name</th>
                      <th className="text-left pb-1 pr-2">1 unit = N {baseUnit || 'base'}</th>
                      <th className="text-left pb-1 pr-2">Price (Rs.)</th>
                      <th className="text-left pb-1 pr-2">Wholesale</th>
                      <th className="text-left pb-1">Default</th>
                      <th className="pb-1"></th>
                    </tr>
                  </thead>
                  <tbody className="space-y-1">
                    {salesUnits.map((su, idx) => (
                      <tr key={idx} className="border-t border-border/30">
                        <td className="pr-2 pt-1.5">
                          <input
                            type="text"
                            value={su.name}
                            onChange={(e) => {
                              const updated = [...salesUnits]
                              updated[idx] = { ...updated[idx], name: e.target.value, id: e.target.value.toLowerCase().replace(/\s+/g, '_') }
                              setSalesUnits(updated)
                            }}
                            placeholder="Bundle"
                            className="w-full px-2 py-1 bg-background border border-border rounded text-xs"
                          />
                        </td>
                        <td className="pr-2 pt-1.5">
                          <input
                            type="number"
                            min={0.001}
                            step="any"
                            value={su.conversionFactor || ''}
                            onChange={(e) => {
                              const updated = [...salesUnits]
                              updated[idx] = { ...updated[idx], conversionFactor: parseFloat(e.target.value) || 1 }
                              setSalesUnits(updated)
                            }}
                            placeholder="500"
                            className="w-full px-2 py-1 bg-background border border-border rounded text-xs"
                          />
                        </td>
                        <td className="pr-2 pt-1.5">
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={su.price || ''}
                            onChange={(e) => {
                              const updated = [...salesUnits]
                              updated[idx] = { ...updated[idx], price: parseFloat(e.target.value) || 0 }
                              setSalesUnits(updated)
                            }}
                            placeholder="0.00"
                            className="w-full px-2 py-1 bg-background border border-border rounded text-xs"
                          />
                        </td>
                        <td className="pr-2 pt-1.5">
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={su.wholesalePrice ?? ''}
                            onChange={(e) => {
                              const updated = [...salesUnits]
                              updated[idx] = { ...updated[idx], wholesalePrice: parseFloat(e.target.value) || null }
                              setSalesUnits(updated)
                            }}
                            placeholder="0.00"
                            className="w-full px-2 py-1 bg-background border border-border rounded text-xs"
                          />
                        </td>
                        <td className="pr-2 pt-1.5 text-center">
                          <input
                            type="radio"
                            name="defaultSalesUnit"
                            checked={!!su.isDefault}
                            onChange={() => {
                              setSalesUnits(salesUnits.map((u, i) => ({ ...u, isDefault: i === idx })))
                            }}
                            className="accent-primary"
                          />
                        </td>
                        <td className="pt-1.5">
                          <button
                            type="button"
                            onClick={() => setSalesUnits(salesUnits.filter((_, i) => i !== idx))}
                            className="p-1 text-red-500 hover:bg-red-500/10 rounded"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <button
              type="button"
              onClick={() => setSalesUnits([...salesUnits, { id: '', name: '', conversionFactor: 1, price: 0, isDefault: salesUnits.length === 0 }])}
              className="text-xs font-semibold text-primary hover:underline flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Add Sales Unit
            </button>
          </div>
        )}
      </div>

      {/* Stock & Inventory */}
      <div className="space-y-3">
        <p className="text-xs font-black uppercase text-muted-foreground tracking-widest">Stock & Inventory</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase mb-1">
              <Package className="w-3 h-3" /> Initial Stock
            </label>
            <input
              type="number"
              min="0"
              value={productData.stock}
              onChange={(e) => setProductData({ ...productData, stock: parseInt(e.target.value) || 0 })}
              className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase mb-1">
              <AlertCircle className="w-3 h-3" /> Min Stock Alert
            </label>
            <input
              type="number"
              min="0"
              value={productData.minStock}
              onChange={(e) => setProductData({ ...productData, minStock: parseInt(e.target.value) || 0 })}
              className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
            />
          </div>

          <div>
            <label className="text-xs font-bold text-muted-foreground uppercase mb-1 block">
              Reorder Level
            </label>
            <input
              type="number"
              min="0"
              value={productData.reorderLevel ?? ''}
              onChange={(e) => setProductData({ ...productData, reorderLevel: parseInt(e.target.value) || undefined })}
              placeholder="Alert when stock ≤"
              className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
            />
          </div>

          <div>
            <label className="text-xs font-bold text-muted-foreground uppercase mb-1 block">
              Reorder Qty
            </label>
            <input
              type="number"
              min="0"
              value={productData.reorderQty ?? ''}
              onChange={(e) => setProductData({ ...productData, reorderQty: parseInt(e.target.value) || undefined })}
              placeholder="Suggested order amount"
              className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase mb-1">
              <Calendar className="w-3 h-3" /> Expiry Date
            </label>
            <input
              type="date"
              value={productData.expiryDate || ''}
              onChange={(e) => setProductData({ ...productData, expiryDate: e.target.value })}
              className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* Profit Margin Display */}
      {productData.price != null && productData.cost != null && productData.price > 0 && (
        <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg space-y-1">
          <div className="flex justify-between items-center">
            <span className="text-sm text-green-600 dark:text-green-400">Retail Margin:</span>
            <span className="font-bold text-green-600 dark:text-green-400">
              {((productData.price - productData.cost) / productData.price * 100).toFixed(1)}%
              (Rs. {(productData.price - productData.cost).toFixed(2)} per unit)
            </span>
          </div>
          {productData.wholesalePrice ? (
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">Wholesale Margin:</span>
              <span className="text-xs font-semibold text-muted-foreground">
                {((productData.wholesalePrice - productData.cost) / productData.wholesalePrice * 100).toFixed(1)}%
                (Rs. {(productData.wholesalePrice - productData.cost).toFixed(2)} per unit)
              </span>
            </div>
          ) : null}
          {productData.discountPrice ? (
            <div className="flex justify-between items-center">
              <span className="text-xs text-yellow-500">Promo Margin:</span>
              <span className="text-xs font-semibold text-yellow-500">
                {((productData.discountPrice - productData.cost) / productData.discountPrice * 100).toFixed(1)}%
                (Rs. {(productData.discountPrice - productData.cost).toFixed(2)} per unit)
              </span>
            </div>
          ) : null}
        </div>
      )}

      {/* Barcode Generation Option */}
      {(productData.barcode || productData.isbn) && (
        <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-bold mb-1 flex items-center gap-2">
                <Printer className="w-4 h-4 text-primary" />
                Generate Barcode Label
              </h4>
              <p className="text-xs text-muted-foreground">
                Create and print barcode labels for this product
              </p>
            </div>
            <button
              onClick={() => setShowBarcodeGenerator(true)}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors flex items-center gap-2"
            >
              <Printer className="w-4 h-4" />
              Generate
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-3 pt-4">
        <button
          onClick={() => bookDetails ? setStep('scan') : setStep('details')}
          className="flex-1 py-3 bg-muted hover:bg-muted/80 rounded-xl font-semibold"
        >
          Back
        </button>
        <button
          onClick={handleSave}
          disabled={!productData.name || isSubmitting}
          className="flex-1 py-3 bg-primary text-primary-foreground rounded-xl font-semibold disabled:opacity-50 glow-effect flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CheckCircle2 className="w-4 h-4" />
              Add to Inventory
            </>
          )}
        </button>
      </div>
    </div>
  )

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="glass-card max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-bold esales-text">Add New Product</h2>
            <p className="text-xs text-muted-foreground mt-1">
              {step === 'scan' && 'Step 1: Scan barcode or enter ISBN'}
              {step === 'details' && 'Step 2: Verify book details'}
              {step === 'pricing' && 'Step 3: Set pricing & stock'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center gap-2 mb-6">
          {['scan', 'details', 'pricing'].map((s, i) => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${step === s ? 'bg-primary text-primary-foreground' :
                ['scan', 'details', 'pricing'].indexOf(step) > i ? 'bg-green-500 text-white' :
                  'bg-muted text-muted-foreground'
                }`}>
                {['scan', 'details', 'pricing'].indexOf(step) > i ? '✓' : i + 1}
              </div>
              {i < 2 && (
                <div className={`flex-1 h-1 rounded ${['scan', 'details', 'pricing'].indexOf(step) > i ? 'bg-green-500' : 'bg-muted'
                  }`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        {step === 'scan' && renderScanStep()}
        {step === 'details' && renderDetailsStep()}
        {step === 'pricing' && renderPricingStep()}
      </div>

      {/* Camera Scanner Modal */}
      {showCameraScanner && (
        <BarcodeScanner
          onScan={(barcode) => {
            setShowCameraScanner(false)
            handleBarcodeScan(barcode)
          }}
          onClose={() => setShowCameraScanner(false)}
          isLoading={isLookingUp}
        />
      )}

      {/* Barcode Generator Modal */}
      {showBarcodeGenerator && (productData.barcode || productData.isbn) && (
        <BarcodeGenerator
          barcode={productData.barcode || productData.isbn || ''}
          productName={productData.name}
          sku={productData.sku}
          onClose={() => setShowBarcodeGenerator(false)}
          onPrint={() => {
            // Optional: Show success message after printing
          }}
        />
      )}
    </div>
  )
}

export default AddProductModal
